import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Plus, Trash2, Check, X, Wallet, ArrowDownCircle, ArrowUpCircle, Scale, Download, Pencil } from "lucide-react";
import { toast } from "sonner";
import { DatePicker } from "@/components/DatePicker";
import { api, brl, downloadCsv, fmtDate, formatApiError, monthLabel, UNITS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const statusStyle = {
  pago: "text-green-400 border-green-500/40",
  pendente: "text-amber-300 border-amber-500/40",
  atrasado: "text-red-400 border-red-500/40",
};

const emptyTx = { type: "receita", description: "", category: "", amount: "", status: "pendente", due_date: "", unit: "iatech" };

const inputCls =
  "w-full bg-zinc-900/80 border border-zinc-700 focus:border-nx-yellow focus:outline-none rounded-xl px-3.5 py-2.5 text-sm text-white transition-colors";

const Field = ({ label, children }) => (
  <div>
    <label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const CategoryCombobox = ({ value, onChange, kind, categories, onCreated, testid }) => {
  const [open, setOpen] = useState(false);
  const trimmed = value.trim();
  const filtered = categories.filter(
    (c) => (c.kind === "ambos" || c.kind === kind) && c.name.toLowerCase().includes(value.toLowerCase())
  );
  const exists = categories.some((c) => c.name.toLowerCase() === trimmed.toLowerCase());

  const createCategory = async () => {
    try {
      const { data } = await api.post("/categories", { name: trimmed, kind: "ambos" });
      onCreated(data);
      onChange(data.name);
      setOpen(false);
      toast.success(`Categoria "${data.name}" cadastrada`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="relative">
      <input
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className={inputCls}
        placeholder="Buscar ou cadastrar categoria"
        data-testid={testid}
        autoComplete="off"
      />
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto bg-[#18181F] border border-zinc-700 rounded-xl shadow-xl" data-testid={`${testid}-dropdown`}>
          {filtered.map((c) => (
            <button
              key={c.id}
              type="button"
              onMouseDown={() => {
                onChange(c.name);
                setOpen(false);
              }}
              data-testid={`tx-category-option-${c.id}`}
              className="w-full text-left px-3.5 py-2.5 text-sm text-zinc-200 hover:bg-zinc-800 flex items-center justify-between gap-2"
            >
              <span>{c.name}</span>
              <span className="text-[10px] font-mono uppercase text-zinc-400 ml-3 shrink-0">{c.kind}</span>
            </button>
          ))}
          {trimmed.length >= 2 && !exists && (
            <button
              type="button"
              onMouseDown={createCategory}
              data-testid="tx-category-add-new"
              className="w-full text-left px-3.5 py-2.5 text-sm text-nx-yellow hover:bg-zinc-800 font-semibold border-t border-zinc-800"
            >
              + Cadastrar "{trimmed}"
            </button>
          )}
          {filtered.length === 0 && (trimmed.length < 2 || exists) && (
            <p className="px-3.5 py-2.5 text-xs text-zinc-500">Nenhuma categoria encontrada.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default function Financeiro() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [txs, setTxs] = useState([]);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [summaryUnit, setSummaryUnit] = useState("");
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(emptyTx);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const loadSummary = () => {
    const params = summaryUnit ? { unit: summaryUnit } : {};
    api.get("/finance/summary", { params }).then((r) => setSummary(r.data)).catch(() => {});
  };
  const loadTxs = () => {
    const params = {};
    if (filterType) params.type = filterType;
    if (filterStatus) params.status = filterStatus;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/transactions", { params }).then((r) => setTxs(r.data)).catch(() => {});
  };

  useEffect(() => {
    loadSummary();
  }, [summaryUnit]);
  useEffect(() => {
    loadTxs();
  }, [filterType, filterStatus, dateFrom, dateTo]);

  const clearFilters = () => {
    setFilterType("");
    setFilterStatus("");
    setDateFrom("");
    setDateTo("");
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyTx);
    setShowForm(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      type: t.type, description: t.description, category: t.category || "",
      amount: String(t.amount), status: t.status, due_date: t.due_date, unit: t.unit,
    });
    setShowForm(true);
  };

  const saveTx = async (e) => {
    e.preventDefault();
    if (!form.due_date) {
      toast.error("Selecione a data de vencimento");
      return;
    }
    try {
      if (editing) {
        await api.patch(`/transactions/${editing.id}`, { ...form, amount: parseFloat(form.amount) });
        toast.success("Lançamento atualizado");
      } else {
        await api.post("/transactions", { ...form, amount: parseFloat(form.amount) });
        toast.success("Lançamento criado");
      }
      setForm(emptyTx);
      setEditing(null);
      setShowForm(false);
      loadSummary();
      loadTxs();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const markPaid = async (id) => {
    try {
      await api.patch(`/transactions/${id}`, { status: "pago" });
      toast.success("Marcado como pago");
      loadSummary();
      loadTxs();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeTx = async (id) => {
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Lançamento removido");
      loadSummary();
      loadTxs();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const kpis = summary
    ? [
        { icon: Wallet, label: "Fluxo de Caixa", value: brl(summary.saldo), testid: "financial-kpi-cashflow", accent: true },
        { icon: ArrowUpCircle, label: "Contas a Receber", value: brl(summary.a_receber), testid: "financial-kpi-receivable" },
        { icon: ArrowDownCircle, label: "Contas a Pagar", value: brl(summary.a_pagar), testid: "financial-kpi-payable" },
        { icon: Scale, label: "Receitas no Mês", value: brl(summary.monthly[summary.monthly.length - 1]?.receitas ?? 0), testid: "financial-kpi-month-revenue" },
      ]
    : [];

  const chartData = summary ? summary.monthly.map((m) => ({ ...m, label: monthLabel(m.month) })) : [];

  return (
    <div className="space-y-6" data-testid="financial-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-nx-yellow">Financeiro</p>
          <h1 className="font-syne text-3xl sm:text-4xl font-extrabold mt-2" data-testid="financial-title">Gestão Financeira</h1>
        </div>
        <button
          onClick={openCreate}
          data-testid="financial-add-transaction-button"
          className="inline-flex items-center gap-2 bg-nx-yellow text-nx-dark text-sm font-bold px-5 py-2.5 rounded-full hover:bg-nx-electric transition-colors"
        >
          <Plus className="w-4 h-4" /> Novo Lançamento
        </button>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.testid} className={`nx-card p-5 ${k.accent ? "border-nx-yellow/40 yellow-glow" : ""}`} data-testid={k.testid}>
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">{k.label}</p>
              <k.icon className={`w-4 h-4 ${k.accent ? "text-nx-yellow" : "text-zinc-500"}`} />
            </div>
            <p className={`font-mono text-lg sm:text-2xl font-bold mt-3 ${k.accent ? "text-nx-yellow" : "text-white"}`} data-testid={`${k.testid}-value`}>
              {k.value}
            </p>
          </div>
        ))}
      </div>

      <div className="nx-card p-6" data-testid="financial-cashflow-chart">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h2 className="font-syne text-lg font-bold">Receitas x Despesas — 6 meses</h2>
          <select value={summaryUnit} onChange={(e) => setSummaryUnit(e.target.value)} className={`${inputCls} !w-auto`} data-testid="financial-summary-unit-filter">
            <option value="">Todas as unidades</option>
            {Object.entries(UNITS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} barGap={6}>
            <CartesianGrid strokeDasharray="3 3" stroke="#272732" />
            <XAxis dataKey="label" stroke="#71717A" fontSize={11} fontFamily="JetBrains Mono" />
            <YAxis stroke="#71717A" fontSize={11} fontFamily="JetBrains Mono" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
            <Tooltip
              contentStyle={{ background: "#18181F", border: "1px solid #272732", borderRadius: 12, fontFamily: "JetBrains Mono", fontSize: 12 }}
              formatter={(v) => brl(v)}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="receitas" name="Receitas" fill="#C6F14A" radius={[6, 6, 0, 0]} />
            <Bar dataKey="despesas" name="Despesas" fill="#52525B" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="nx-card" data-testid="financial-transactions-table">
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-zinc-800">
          <h2 className="font-syne text-lg font-bold mr-auto">Lançamentos</h2>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className={`${inputCls} !w-auto`} data-testid="financial-filter-type">
            <option value="">Todos os tipos</option>
            <option value="receita">Receitas</option>
            <option value="despesa">Despesas</option>
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={`${inputCls} !w-auto`} data-testid="financial-filter-status">
            <option value="">Todos os status</option>
            <option value="pago">Pago</option>
            <option value="pendente">Pendente</option>
            <option value="atrasado">Atrasado</option>
          </select>
          <div className="flex items-center gap-2">
            <div className="w-[150px]">
              <DatePicker value={dateFrom} onChange={setDateFrom} testid="financial-filter-date-from" />
            </div>
            <span className="text-zinc-500 text-xs">até</span>
            <div className="w-[150px]">
              <DatePicker value={dateTo} onChange={setDateTo} testid="financial-filter-date-to" />
            </div>
          </div>
          {(filterType || filterStatus || dateFrom || dateTo) && (
            <button
              onClick={clearFilters}
              data-testid="financial-clear-filters"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-400 hover:text-nx-yellow transition-colors"
            >
              <X className="w-3.5 h-3.5" /> Limpar
            </button>
          )}
          <button
            onClick={() => downloadCsv("/transactions/export", "lancamentos.csv")}
            data-testid="financial-export-csv"
            className="inline-flex items-center gap-2 border border-zinc-700 hover:border-nx-yellow hover:text-nx-yellow text-xs font-semibold px-4 py-2.5 rounded-xl transition-colors"
          >
            <Download className="w-3.5 h-3.5" /> Exportar CSV
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[780px]">
            <thead>
              <tr className="border-b border-zinc-800 text-left">
                {["Descrição", "Categoria", "Unidade", "Vencimento", "Valor", "Status", "Ações"].map((h) => (
                  <th key={h} className="px-5 py-4 text-[11px] font-mono uppercase tracking-widest text-zinc-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {txs.map((t) => (
                <tr key={t.id} className="border-b border-zinc-800/50 last:border-0 hover:bg-zinc-900/40" data-testid={`tx-row-${t.id}`}>
                  <td className="px-5 py-3.5 font-semibold">{t.description}</td>
                  <td className="px-5 py-3.5 text-zinc-400 text-xs">{t.category}</td>
                  <td className="px-5 py-3.5 text-zinc-400 text-xs">{UNITS[t.unit]}</td>
                  <td className="px-5 py-3.5 font-mono text-xs text-zinc-400">{fmtDate(t.due_date)}</td>
                  <td className={`px-5 py-3.5 font-mono text-xs font-bold ${t.type === "receita" ? "text-nx-yellow" : "text-zinc-400"}`}>
                    {t.type === "receita" ? "+" : "−"} {brl(t.amount)}
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`text-[11px] font-mono uppercase border rounded-full px-2.5 py-1 ${statusStyle[t.status]}`}>
                      {t.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <button onClick={() => openEdit(t)} title="Editar" data-testid={`tx-edit-${t.id}`} className="text-zinc-400 hover:text-nx-yellow transition-colors">
                        <Pencil className="w-4 h-4" />
                      </button>
                      {t.status !== "pago" && (
                        <button onClick={() => markPaid(t.id)} title="Marcar como pago" data-testid={`tx-mark-paid-${t.id}`} className="text-zinc-600 hover:text-green-400 transition-colors">
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      {user?.role === "admin" && (
                        <button onClick={() => removeTx(t.id)} title="Excluir" data-testid={`tx-delete-${t.id}`} className="text-zinc-600 hover:text-red-400 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {txs.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-zinc-500">Nenhum lançamento encontrado.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Novo Lançamento */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="transaction-form-modal">
          <form onSubmit={saveTx} className="glass rounded-3xl p-7 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-syne text-xl font-bold">{editing ? "Editar Lançamento" : "Novo Lançamento"}</h2>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} data-testid="transaction-form-close" className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Tipo *">
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls} data-testid="tx-type-select">
                  <option value="receita">Receita</option>
                  <option value="despesa">Despesa</option>
                </select>
              </Field>
              <Field label="Valor (R$) *">
                <input required type="number" min="0.01" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputCls} data-testid="tx-amount-input" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Descrição *">
                  <input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} data-testid="tx-description-input" />
                </Field>
              </div>
              <Field label="Categoria">
                <CategoryCombobox
                  value={form.category}
                  kind={form.type}
                  categories={categories}
                  onCreated={(c) => setCategories((cs) => [...cs, c])}
                  onChange={(v) => setForm({ ...form, category: v })}
                  testid="tx-category-input"
                />
              </Field>
              <Field label="Vencimento *">
                <DatePicker value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} testid="tx-due-date-input" />
              </Field>
              <Field label="Unidade">
                <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className={inputCls} data-testid="tx-unit-select">
                  {Object.entries(UNITS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls} data-testid="tx-status-select">
                  <option value="pendente">Pendente</option>
                  <option value="pago">Pago</option>
                  <option value="atrasado">Atrasado</option>
                </select>
              </Field>
            </div>
            <button type="submit" data-testid="tx-submit-button" className="mt-6 w-full bg-nx-yellow text-nx-dark font-bold py-3 rounded-xl hover:bg-nx-electric transition-colors">
              {editing ? "Salvar alterações" : "Salvar lançamento"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
